import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import CampaignsAPI from '../../api/campaigns';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { CAMPAIGNS_EVENTS } from '../../helper/AnalyticsHelper/events';
import { CAMPAIGN_TYPES } from 'shared/constants/campaign';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isTransitioning: false,
    isFetchingRecipients: false,
    isFetchingAnalytics: false,
  },
  recipients: {
    payload: [],
    meta: {},
  },
  analytics: null,
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getCampaigns:
    _state =>
    (campaignType, inboxChannelTypes = null) => {
      let filteredRecords = _state.records.filter(
        record => record.campaign_type === campaignType
      );

      if (inboxChannelTypes && Array.isArray(inboxChannelTypes)) {
        filteredRecords = filteredRecords.filter(record => {
          return (
            record.inbox &&
            inboxChannelTypes.includes(record.inbox.channel_type)
          );
        });
      }

      return filteredRecords.sort((a1, a2) => a1.id - a2.id);
    },
  getSMSCampaigns: (_state, _getters) => {
    const smsChannelTypes = [INBOX_TYPES.SMS, INBOX_TYPES.TWILIO];
    return _getters.getCampaigns(CAMPAIGN_TYPES.ONE_OFF, smsChannelTypes);
  },
  getWhatsAppCampaigns: (_state, _getters) => {
    const whatsappChannelTypes = [INBOX_TYPES.WHATSAPP];
    return _getters.getCampaigns(CAMPAIGN_TYPES.ONE_OFF, whatsappChannelTypes);
  },
  getLiveChatCampaigns: (_state, _getters) => {
    const liveChatChannelTypes = [INBOX_TYPES.WEB];
    return _getters.getCampaigns(CAMPAIGN_TYPES.ONGOING, liveChatChannelTypes);
  },
  getAllCampaigns: _state => {
    return _state.records;
  },
  getCampaign: _state => id => {
    return _state.records.find(record => record.id === Number(id));
  },
  getCampaignRecipients: _state => _state.recipients.payload,
  getCampaignRecipientsMeta: _state => _state.recipients.meta,
  getCampaignAnalytics: _state => _state.analytics,
};

export const actions = {
  get: async function getCampaigns({ commit }) {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: true });
    try {
      const response = await CampaignsAPI.get();
      commit(types.SET_CAMPAIGNS, response.data);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: false });
    }
  },
  // Upserts (not a plain push) so a direct navigation/refresh of the detail
  // page - where `records` may not have been populated by the list page's
  // `get` yet - still ends up with exactly one copy of this campaign.
  fetchOne: async function fetchCampaign({ commit }, id) {
    const response = await CampaignsAPI.show(id);
    commit(types.SET_SINGLE_CAMPAIGN, response.data);
    return response.data;
  },
  create: async function createCampaign({ commit }, campaignObj) {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: true });
    try {
      const response = await CampaignsAPI.create(campaignObj);
      commit(types.ADD_CAMPAIGN, response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: false });
    }
  },
  update: async ({ commit }, { id, ...updateObj }) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: true });
    try {
      const response = await CampaignsAPI.update(id, updateObj);
      AnalyticsHelper.track(CAMPAIGNS_EVENTS.UPDATE_CAMPAIGN);
      commit(types.EDIT_CAMPAIGN, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: false });
    }
  },
  delete: async ({ commit }, id) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: true });
    try {
      await CampaignsAPI.delete(id);
      AnalyticsHelper.track(CAMPAIGNS_EVENTS.DELETE_CAMPAIGN);
      commit(types.DELETE_CAMPAIGN, id);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: false });
    }
  },
  // WhatsApp V1 state-machine transitions - the backend is the sole
  // authority on legality (Campaign::V1_TRANSITIONS /
  // Campaigns::CampaignStateService); the frontend never sets
  // campaign_status directly, it only calls these and applies whatever
  // state the response comes back with.
  schedule: async ({ commit }, { id, scheduledAt }) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isTransitioning: true });
    try {
      const response = await CampaignsAPI.schedule(id, scheduledAt);
      commit(types.EDIT_CAMPAIGN, response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isTransitioning: false });
    }
  },
  sendNow: async ({ commit }, id) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isTransitioning: true });
    try {
      const response = await CampaignsAPI.sendNow(id);
      commit(types.EDIT_CAMPAIGN, response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isTransitioning: false });
    }
  },
  pause: async ({ commit }, id) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isTransitioning: true });
    try {
      const response = await CampaignsAPI.pause(id);
      commit(types.EDIT_CAMPAIGN, response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isTransitioning: false });
    }
  },
  resume: async ({ commit }, id) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isTransitioning: true });
    try {
      const response = await CampaignsAPI.resume(id);
      commit(types.EDIT_CAMPAIGN, response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isTransitioning: false });
    }
  },
  cancel: async ({ commit }, id) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isTransitioning: true });
    try {
      const response = await CampaignsAPI.cancel(id);
      commit(types.EDIT_CAMPAIGN, response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isTransitioning: false });
    }
  },
  retryFailed: async ({ commit }, id) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isTransitioning: true });
    try {
      const response = await CampaignsAPI.retryFailed(id);
      commit(types.EDIT_CAMPAIGN, response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isTransitioning: false });
    }
  },
  fetchRecipients: async ({ commit }, { id, status, page }) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isFetchingRecipients: true });
    try {
      const response = await CampaignsAPI.recipients(id, { status, page });
      commit(types.SET_CAMPAIGN_RECIPIENTS, response.data);
      return response.data;
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isFetchingRecipients: false });
    }
  },
  fetchAnalytics: async ({ commit }, id) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isFetchingAnalytics: true });
    try {
      const response = await CampaignsAPI.analytics(id);
      commit(types.SET_CAMPAIGN_ANALYTICS, response.data);
      return response.data;
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isFetchingAnalytics: false });
    }
  },
  // Pure read-only utility - deliberately does not commit any state, since
  // the count is only ever needed transiently while the creation wizard's
  // audience step is open. Never resolved client-side: the backend re-uses
  // Campaigns::AudienceResolutionService for this exact call.
  estimateAudience: async (_context, audienceConfig) => {
    const response = await CampaignsAPI.estimateAudience(audienceConfig);
    return response.data.count;
  },
};

export const mutations = {
  [types.SET_CAMPAIGN_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.ADD_CAMPAIGN]: MutationHelpers.create,
  [types.SET_CAMPAIGNS]: MutationHelpers.set,
  [types.EDIT_CAMPAIGN]: MutationHelpers.update,
  [types.DELETE_CAMPAIGN]: MutationHelpers.destroy,
  [types.SET_SINGLE_CAMPAIGN]: MutationHelpers.setSingleRecord,

  [types.SET_CAMPAIGN_RECIPIENTS](_state, { meta, payload }) {
    _state.recipients = { meta, payload };
  },
  [types.SET_CAMPAIGN_ANALYTICS](_state, data) {
    _state.analytics = data;
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
